/**
 * Projects Table — Vulnerability Management dashboard.
 *
 * Renders a 3-level expandable list of projects, components, and
 * versions via the canonical DataTable primitive
 * (`components/ui/DataTable/DataTable.jsx`). The 687-line hand-rolled
 * predecessor used a bespoke `<table>` with three separate row
 * renderers + a custom toolbar / pagination + inline icon strips; the
 * migration moves everything onto the shared chrome so the visual
 * matches every other Athena table page.
 *
 * Tree shape:
 *   - depth 0: project rows (top-level array passed as `data`)
 *   - depth 1: component groups under an expanded project
 *   - depth 2: version rows under an expanded multi-version component
 *
 * DataTable's `getRowChildren(row, depth)` declares the tree;
 * `expandedIds` + `onExpandedChange` keep the chevron state controlled
 * locally. Each level's content is rendered via the `column.render(row,
 * ctx)` second-arg context — depth drives the Name column's labelling
 * (Project vs Component-with-version-pill vs Version) and which
 * actions the kebab + tracking toggle expose.
 */
import React, { useMemo, useState, memo, useCallback } from 'react';
import DataTable from '../../components/ui/DataTable/DataTable';
import TableToolbar from '../../components/ui/DataTable/TableToolbar';
import TablePagination from '../../components/ui/DataTable/TablePagination';
import RowActionsMenu from '../../components/ui/DataTable/RowActionsMenu';
import { Button } from '../../components/ui/Button2';
import dtStyles from '../../components/ui/DataTable/DataTable.module.css';
import VulnBar from './components/VulnBar';
import styles from './SbomAnalysisTable.module.css';

// Pre-process rows so every level has a unique key DataTable can use
// (project.uuid is unique at depth 0, but component groups don't ship
// with a stable id of their own — synthesise one from the parent's
// uuid + componentName so React keys + expandedIds stay stable).
const decorate = (projects) =>
  projects.map((p) => ({
    ...p,
    _rowKey: p.uuid,
    _level: 'project',
    componentGroups: (p.componentGroups ?? []).map((g) => ({
      ...g,
      _rowKey: `${p.uuid}:${g.componentName}`,
      _level: 'component',
      _projectUuid: p.uuid,
      versions: (g.versions ?? []).map((v) => ({
        ...v,
        _rowKey: v.uuid,
        _level: 'version',
        _projectUuid: p.uuid,
        _componentName: g.componentName,
      })),
    })),
  }));

// SVG icon helpers (small inline svgs — kebab menu icons + the
// tracking-toggle check + the action-cell trigger icons).
const Icon = ({ children, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {children}
  </svg>
);
const UploadIcon = () => <Icon><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" /></Icon>;
const DeleteIcon = () => <Icon><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" /></Icon>;
const ViewIcon = () => <Icon><><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></></Icon>;
const CheckIcon = ({ filled }) => (
  <Icon size={16}><path d="M20 6L9 17l-5-5" opacity={filled ? 1 : 0.4} /></Icon>
);

const ProjectsTable = memo(({
  projects = [],
  total = 0,
  currentPage = 1,
  pageSize = 20,
  loading = false,
  onPageChange,
  onPageSizeChange,
  onNewProjectClick,
  onUploadScanClick,
  onDeleteProjectClick,
  onDeleteScanClick,
  onToggleTracked,
  trackingInProgress = new Set(),
  onVersionClick,
}) => {
  const [expandedIds, setExpandedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState(null);

  const decoratedProjects = useMemo(() => decorate(projects), [projects]);

  // Filter then sort. Sorting only reorders the TOP-LEVEL projects — child
  // rows (components + versions) stay grouped under their parent so the
  // tree reads naturally regardless of sort direction.
  const filteredProjects = useMemo(() => {
    let result = decoratedProjects;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((p) => p.name?.toLowerCase().includes(q));
    }
    if (sort) {
      const dir = sort.direction === 'desc' ? -1 : 1;
      const cmpString = (a, b) => {
        const av = (a ?? '').toString().toLowerCase();
        const bv = (b ?? '').toString().toLowerCase();
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      };
      const cmpNumber = (a, b) => ((a ?? 0) - (b ?? 0)) * dir;
      result = [...result].sort((a, b) => {
        if (sort.column === 'name') return cmpString(a.name, b.name);
        if (sort.column === 'components') return cmpNumber(a.components, b.components);
        if (sort.column === 'vulnerabilities') return cmpNumber(a.vulnerabilities, b.vulnerabilities);
        return 0;
      });
    }
    return result;
  }, [decoratedProjects, searchQuery, sort]);

  // Tree shape: project → componentGroups → multi-version components only
  // expand into versions (single-version components are treated as leaves,
  // matching the pre-migration behaviour).
  const getRowChildren = useCallback((row, depth) => {
    if (depth === 0) {
      return row.componentGroups?.length ? row.componentGroups : null;
    }
    if (depth === 1) {
      return row.versions?.length > 1 ? row.versions : null;
    }
    return null;
  }, []);

  // Selecting a leaf (single-version component, or any version row)
  // navigates to its scan report.
  const handleRowClick = useCallback(
    (row) => {
      if (row._level === 'component' && row.versions?.length === 1) {
        const v = row.versions[0];
        onVersionClick?.(v.uuid, v.isLatest, row.versions);
      } else if (row._level === 'version') {
        const allVersions = decoratedProjects
          .find((p) => p.uuid === row._projectUuid)
          ?.componentGroups?.find((g) => g.componentName === row._componentName)
          ?.versions;
        onVersionClick?.(row.uuid, row.isLatest, allVersions);
      }
    },
    [decoratedProjects, onVersionClick],
  );

  // Per-row vuln summary — projects + versions store the numbers directly;
  // component groups borrow from their first version (matches the
  // pre-migration `latestVersion` fallback).
  const vulnSummary = (row) => {
    if (row._level === 'component') {
      const v = row.versions?.find((x) => x.isLatest) ?? row.versions?.[0] ?? {};
      return {
        total: v.vulnerabilities,
        critical: v.critical,
        high: v.high,
        medium: v.medium,
        low: v.low,
      };
    }
    return {
      total: row.vulnerabilities,
      critical: row.critical,
      high: row.high,
      medium: row.medium,
      low: row.low,
    };
  };

  const componentCount = (row) => {
    if (row._level === 'component') {
      const v = row.versions?.find((x) => x.isLatest) ?? row.versions?.[0];
      return v?.components ?? '—';
    }
    return row.components ?? 0;
  };

  // Tracking toggle — visible icon button. Stays inline (not in the
  // kebab) because the on/off state IS the affordance.
  const renderTrackingToggle = (version) => {
    if (trackingInProgress.has(version.uuid)) {
      return (
        <span className={styles.spinnerWrap} aria-label="Updating tracking state">
          <span className={styles.spinner} />
        </span>
      );
    }
    const tracked = version.isLatest;
    return (
      <button
        type="button"
        className={`${styles.trackingBtn} ${tracked ? styles.trackingBtnOn : styles.trackingBtnOff}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleTracked?.(version.uuid, !tracked);
        }}
        title={tracked ? 'Tracked — click to untrack' : 'Untracked — click to track'}
        aria-label={tracked ? 'Untrack version' : 'Track version'}
        aria-pressed={tracked}
      >
        <CheckIcon filled={tracked} />
      </button>
    );
  };

  // Per-level RowActionsMenu items. Tracking toggle stays separate
  // (rendered alongside the kebab in the same actions cell).
  const buildActions = (row) => {
    if (row._level === 'project') {
      const items = [];
      if (onUploadScanClick) {
        items.push({ id: 'upload', label: 'Upload scan', icon: <UploadIcon />, onClick: () => onUploadScanClick(row.uuid) });
      }
      if (onDeleteProjectClick) {
        items.push({ id: 'delete', label: 'Delete project', icon: <DeleteIcon />, danger: true, onClick: () => onDeleteProjectClick(row.uuid) });
      }
      return items;
    }
    // Component / version actions — bound to the leaf version (single-version
    // component case) or to the version row itself.
    const version = row._level === 'version'
      ? row
      : row.versions?.find((v) => v.isLatest) ?? row.versions?.[0];
    if (!version) return [];
    const items = [];
    items.push({
      id: 'view',
      label: 'View report',
      icon: <ViewIcon />,
      onClick: () => {
        const allVersions = row._level === 'version'
          ? decoratedProjects
              .find((p) => p.uuid === row._projectUuid)
              ?.componentGroups?.find((g) => g.componentName === row._componentName)
              ?.versions
          : row.versions;
        onVersionClick?.(version.uuid, version.isLatest, allVersions);
      },
    });
    if (onDeleteScanClick) {
      items.push({ id: 'delete', label: 'Delete scan', icon: <DeleteIcon />, danger: true, onClick: () => onDeleteScanClick(version.uuid) });
    }
    return items;
  };

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '40%',
      render: (row) => {
        if (row._level === 'project') return row.name;
        if (row._level === 'component') {
          const v = row.versions?.find((x) => x.isLatest) ?? row.versions?.[0];
          return (
            <span>
              {row.componentName}
              {v?.version && <span className={styles.versionPill}>v{v.version}</span>}
            </span>
          );
        }
        return <span>v{row.version}</span>;
      },
    },
    {
      key: 'components',
      label: 'Components',
      sortable: true,
      width: '12%',
      align: 'center',
      render: (row) => componentCount(row),
    },
    {
      key: 'vulnerabilities',
      label: 'Vulnerabilities',
      sortable: true,
      width: '30%',
      align: 'center',
      render: (row) => <VulnBar {...vulnSummary(row)} />,
    },
    {
      key: '_actions',
      label: '',
      width: '18%',
      align: 'right',
      render: (row) => {
        const showTracking = row._level === 'version'
          || (row._level === 'component' && row.versions?.length === 1);
        const trackVersion = row._level === 'version'
          ? row
          : row.versions?.[0];
        const actions = buildActions(row);
        return (
          <span className={styles.actionsCell}>
            {showTracking && trackVersion && renderTrackingToggle(trackVersion)}
            {actions.length > 0 && (
              <RowActionsMenu actions={actions} ariaLabel="Row actions" />
            )}
          </span>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [trackingInProgress, decoratedProjects]);

  return (
    <div className={dtStyles.shell}>
    <DataTable
      data={filteredProjects}
      columns={columns}
      rowKey="_rowKey"
      loading={loading}
      sort={sort}
      onSortChange={(column, direction) => setSort(column ? { column, direction } : null)}
      onRowClick={handleRowClick}
      getRowChildren={getRowChildren}
      expandedIds={expandedIds}
      onExpandedChange={setExpandedIds}
      emptyState={{
        icon: (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="currentColor" opacity="0.5" />
          </svg>
        ),
        title: searchQuery ? 'No matching projects' : 'No projects found',
        description: searchQuery
          ? 'Try a different search term'
          : 'Start by creating your first project',
      }}
      toolbar={
        <TableToolbar
          search={{
            value: searchQuery,
            onChange: setSearchQuery,
            placeholder: 'Search projects...',
          }}
          showFilterButton={false}
          count={{ value: filteredProjects.length, label: 'projects' }}
          actions={
            onNewProjectClick && (
              <Button
                variant="default"
                onClick={onNewProjectClick}
                startIcon={<Icon><path d="M12 4V20M20 12H4" /></Icon>}
              >
                New Project
              </Button>
            )
          }
        />
      }
      pagination={
        <TablePagination
          currentPage={currentPage}
          totalItems={total}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
          onItemsPerPageChange={onPageSizeChange}
        />
      }
    />
    </div>
  );
});

ProjectsTable.displayName = 'ProjectsTable';

export default ProjectsTable;
